import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/redwolfSdkAPI';
import { checkEmailFormat } from '../../utils/validations';
import { useRecaptcha } from '../common/hooks/useRecaptcha';
import { checkEmailIdExits } from '../../utils/accessRedirection';
import Spinner from '../common/components/Spinner';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';
import useHeadingFocuser from '../common/hooks/useHeadingFocuser';
import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import { addClasses } from '../../utils/eventListeners';
import * as Sentry from '@sentry/react';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';

const PasswordReset = () => {
	const [registeredEmail, setRegisteredEmail] = useState('');
	const [errorMsgs, setErrorMsgs] = useState({ emailError: '' });
	const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(true);
	const [isUnableFindEmail, setIsUnableFindEmail] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isPasswordResetSuccess, setIsPasswordResetSuccess] = useState(false);
	const [isRecaptchaLoading, generateRecaptchaToken]: any = useRecaptcha();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [headingRef] = useHeadingFocuser();
	const emailRef = useRef<HTMLInputElement>(null);

	const transaction = Sentry.startInactiveSpan({ name: 'Password Reset' });

	useEffect(() => {
		addClasses();
	}, []);

	useEffect(() => {
		setIsSpinnerLoading(!isRecaptchaLoading);
	}, [isRecaptchaLoading]);

	const registeredEmailHandler = (event: any) => {
		const { value } = event.target;
		setRegisteredEmail(value);
		setErrorMsgs({ emailError: '' });
		setIsUnableFindEmail(false);
		setIsSubmitButtonEnabled(false);
	};

	const checkEmailExits = async (event: any) => {
		const valid = checkEmailFormat(registeredEmail);
		if (!registeredEmail) {
			setErrorMsgs({
				emailError: 'This is a required field.'
			});
			setIsSubmitButtonEnabled(true);
		} else if (!valid) {
			setErrorMsgs({
				emailError: 'Please enter a valid email address.'
			});
			setIsSubmitButtonEnabled(true);
		} else {
			event.preventDefault();
			setIsSpinnerLoading(true);

			const captchaToken = await generateRecaptchaToken('vz_account_check_email_exits').then(
				(response: any) => response
			);

			checkEmailIdExits(registeredEmail, captchaToken)
				.then((isEmailExists: any) => {
					if (isEmailExists) {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_CHECK', { item_title: 'valid' });
						setIsSubmitButtonEnabled(false);

						handleSubmit();
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_CHECK', { item_title: 'invalid' });
						setIsSpinnerLoading(false);
						setIsUnableFindEmail(true);
						setIsSubmitButtonEnabled(true);
						transaction && transaction.end();
						if (emailRef.current !== null) {
							emailRef.current.focus();
						}
					}
				})
				.catch((err: any) => {
					setIsSpinnerLoading(false);
					transaction && transaction.end();
					showTechnicalErrorMessage();
					Sentry.captureException(err);
				});
		}
	};

	const handleSubmit = async () => {
		const valid = checkEmailFormat(registeredEmail);
		if (!registeredEmail) {
			setErrorMsgs({
				emailError: 'This is a required field.'
			});
			setIsSubmitButtonEnabled(true);
		} else if (!valid) {
			setErrorMsgs({
				emailError: 'Please enter a valid email address.'
			});
			setIsSubmitButtonEnabled(true);
		} else {
			const captchaToken = await generateRecaptchaToken('vz_account_forgot_password').then(
				(response: any) => response
			);

			requestRedwolfService(
				authAPI.forgotPasswordResetRequest({ email: registeredEmail, reCaptchaToken: captchaToken })
			)
				.then((response: any) => {
					setIsSpinnerLoading(false);
					if (response.constructor.$metadata$.simpleName === 'Success') {
						setIsPasswordResetSuccess(true);
					} else {
						if (response.response && response.response.status != 400) {
							Sentry.captureException(new Error('forgotPasswordHandleSubmit:' + response));
						}

						showTechnicalErrorMessage();
					}
					transaction && transaction.end();
				})
				.catch((err: any) => {
					setIsSpinnerLoading(false);
					transaction && transaction.end();
					Sentry.captureException(err);
					if (emailRef.current !== null) {
						emailRef.current.focus();
					}
					showTechnicalErrorMessage();
				});
		}
	};

	const validateEmailOnBlur = () => {
		const valid = checkEmailFormat(registeredEmail);
		if (!registeredEmail) {
			setErrorMsgs({
				emailError: 'This is a required field.'
			});
			setIsSubmitButtonEnabled(true);
		} else if (!valid) {
			setErrorMsgs({
				emailError: 'Please enter a valid email address.'
			});
			setIsSubmitButtonEnabled(true);
		}
	};

	return (
		<>
			{!isPasswordResetSuccess ? (
				<section className="account-forgot-password">
					<h1 aria-live="polite" ref={headingRef} tabIndex={-1}>
						<span className="design-bord"></span>Forgot your password?
					</h1>

					<form method="post" id="form-validate-password" className="form-forgot-password">
						<p className="login-tagline">
							Please enter your email address below.{' '}
							<span>You will receive an email to reset your password.</span>
						</p>

						<ul className="form-list">
							<li
								className={`field ${isUnableFindEmail || errorMsgs.emailError ? 'has-error' : ''}`}>
								<div className="input-box form-group input-label">
									<input
										id="email"
										type="text"
										className="form-control input-text required-entry validate-email login-field-transparent"
										aria-describedby="unableFindEmailError emailError"
										aria-required="true"
										aria-invalid="false"
										onChange={registeredEmailHandler}
										onBlur={validateEmailOnBlur}
										ref={emailRef}
										spellCheck="false"
										data-testid="email"
										autoComplete="email"
									/>
									<label htmlFor="email" className="control-label">
										Email
									</label>

									<code id="emailError" aria-atomic="true" aria-live="assertive">
										{errorMsgs.emailError}
									</code>

									<code id="unableFindEmailError">
										{isUnableFindEmail && (
											<span aria-live="polite">
												Unable to find email.{' '}
												<Link
													to={{ pathname: '/sign-up' }}
													state={{ email: registeredEmail }}
													className="link">
													Create Account.
												</Link>
											</span>
										)}
									</code>
								</div>
							</li>
						</ul>

						<div className="buttons-set form-buttons">
							<button
								type="submit"
								id="send-mail"
								className="primary-button btn-send-email"
								disabled={isSubmitButtonEnabled}
								onClick={checkEmailExits}>
								Send Email
							</button>
						</div>
					</form>

					{isSpinnerLoading && <Spinner />}
				</section>
			) : (
				<section className="checkmail-wrap">
					<h1 style={{ textAlign: 'center' }}>Check your email</h1>
					<div className="form-forgot-password" role="alert" aria-live="polite">
						An email has been sent with instructions on how to reset your password.
					</div>
				</section>
			)}
		</>
	);
};

export default PasswordReset;
